import { Card } from '@/components/ui/card'
import { PageHeader } from '@/components/atoms'
import { Menu } from 'lucide-react'
import { useState } from 'react'

import { MediaLibraryPage, type MediaFile as LibMediaFile } from '@iws/media-library'
import { createMediaService } from '@/lib/media/mediaService'
import { resolveMediaUrl } from '@/lib/media/resolveUrl'

import { useToast } from '@/hooks/useToast'

import { MediaSidebar } from '@/components/media/MediaSidebar'
import { VaultSettings } from '@/components/media/VaultSettings'
import { FileDetailsDialog } from '@/components/media/FileDetailsDialog'
import { ImageOptimizerDialog } from '@/components/media/ImageOptimizerDialog'
import { FileConverterDialog } from '@/components/media/FileConverterDialog'

import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'

import { MediaFile } from '@/types/media'

const service = createMediaService()

export const MediaLibrary = () => {
  const { toast } = useToast()

  const [activeSection, setActiveSection] = useState<'files' | 'favorites' | 'trash' | 'settings'>('files')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [optimizerOpen, setOptimizerOpen] = useState(false)
  const [converterOpen, setConverterOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null)

  const mapToMediaFile = (file: LibMediaFile): MediaFile => ({
    id: file._id,
    name: file.name,
    filename: file.name,
    url: resolveMediaUrl(file.path),
    type: file.type.includes('image')
      ? 'image'
      : file.type.includes('video')
        ? 'video'
        : 'document',
    mimeType: file.type,
    size: file.size,
    dimensions: { width: file.width || 0, height: file.height || 0 },
    uploadedBy: 'Admin',
    createdAt: new Date(),
    updatedAt: new Date(),
  })

  const handleOptimize = (file: LibMediaFile) => {
    setSelectedFile(mapToMediaFile(file))
    setOptimizerOpen(true)
  }

  const handleConvert = (file: LibMediaFile) => {
    setSelectedFile(mapToMediaFile(file))
    setConverterOpen(true)
  }

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
    toast({
      title: 'URL copied',
      description: 'Link copied to clipboard',
    })
  }

  const onOptimizeComplete = (_fileId: string, settings: any) => {
    toast({
      title: 'Optimization complete',
      description: `Image optimized with quality ${settings.quality}%`,
    })
  }

  const onConvertComplete = (_fileId: string, targetFormat: string) => {
    toast({
      title: 'Conversion complete',
      description: `File converted to ${targetFormat.toUpperCase()}`,
    })
  }

  return (
    <>
    <PageHeader title="Files" description="Images, videos and documents used across your store." />
    <div className="flex flex-col gap-4 lg:h-[calc(100vh-15rem)] lg:min-h-[560px] lg:flex-row">
      {/* Desktop Sidebar (left nav) */}
      <Card className="hidden w-60 shrink-0 self-start p-2 lg:flex">
        <MediaSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onNavigateRoot={() => {}}
        />
      </Card>

      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-[280px] p-4 pt-12">
          <MediaSidebar
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            onNavigateRoot={() => setSidebarOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <Card className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {activeSection === 'settings' ? (
          <div className="flex-1 space-y-4 overflow-auto p-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Open folders"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="size-4" />
              </Button>
              <h2 className="text-heading-md text-ink">Settings</h2>
            </div>
            <VaultSettings />
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Mobile sidebar toggle */}
            <div className="border-b border-border-subtle px-2 py-2 lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open folders"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="size-4" />
              </Button>
            </div>

            {/* Core media library (uses shared package) */}
            <MediaLibraryPage
              service={service}
              resolveUrl={resolveMediaUrl}
              onOptimize={handleOptimize}
              onConvert={handleConvert}
            />
          </div>
        )}
      </Card>

      {/* Admin-specific dialogs */}
      <FileDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        file={selectedFile}
        onCopyUrl={handleCopyUrl}
        onDelete={() => {}}
        onOptimize={handleOptimize as any}
        onConvert={handleConvert as any}
      />
      <ImageOptimizerDialog
        open={optimizerOpen}
        onOpenChange={setOptimizerOpen}
        file={selectedFile}
        onOptimize={onOptimizeComplete}
      />
      <FileConverterDialog
        open={converterOpen}
        onOpenChange={setConverterOpen}
        file={selectedFile}
        onConvert={onConvertComplete}
      />
    </div>
    </>
  )
}
